import type {
  AdminInfoCookie,
  SupportMember,
  FirestoreTimestamp,
} from "../../interfaces/Messages/chatTypes";
import { TUTOR_CLASS_CHANNELS_COLLECTION } from "../../config/Messages/globalConfig";
import { useAuthStore } from "../../stores/Messages/authStore";
import type { SupportedLanguage } from "../../stores/localizationContext";

/** Response shape from login API (support chat legacy) */
export interface AdminInfoAPIResponse {
  user_id: number;
  user_email?: string;
  user_name?: string;
  firebase_token: string;
  user_roles?: string[];
}

// Channel configuration types
export type ChannelType = 'tutor' | 'student';
export type ChannelsCollectionName =
  | "Tutor_Support_Channels"
  | typeof TUTOR_CLASS_CHANNELS_COLLECTION;

/** Map channel document class_type to ChannelType */
export const getChannelTypeFromClassType = (classType: string | undefined | null): ChannelType | null => {
  if (classType === "Tutor Support") return "tutor";
  if (classType === "Student Support") return "student";
  return null;
};

/** URL segment for messages UI (no tutor_chat / student_chat in path). */
export const MESSAGES_PATH_SEGMENT = "messages";

export function isSupportedLanguage(segment: string): segment is SupportedLanguage {
  return segment === "en" || segment === "ar";
}

/** Build localized messages path: /{lang}/messages or /{lang}/messages/{chatId} */
export function buildMessagesPath(lang: SupportedLanguage, chatId?: string): string {
  const base = `/${lang}/${MESSAGES_PATH_SEGMENT}`;
  const trimmed = chatId?.trim();
  if (trimmed) {
    return `${base}/${encodeURIComponent(trimmed)}`;
  }
  return base;
}

export function isMessagesPathname(pathname: string, lang?: SupportedLanguage): boolean {
  const parts = pathname.split("/").filter(Boolean);
  if (parts[0] === MESSAGES_PATH_SEGMENT) {
    return lang === undefined;
  }
  if (isSupportedLanguage(parts[0]) && parts[1] === MESSAGES_PATH_SEGMENT) {
    return lang === undefined || parts[0] === lang;
  }
  return false;
}

export const getPathPrefixForCollection = (
  lang: SupportedLanguage,
  _collection: ChannelsCollectionName
): string => `${lang}/${MESSAGES_PATH_SEGMENT}`;

export interface ChannelConfig {
  chatId: string;
  language: string;
  channelType: ChannelType;
  channelsCollectionName: ChannelsCollectionName;
}

const defaultChannelConfig = (chatId: string, language: SupportedLanguage): ChannelConfig => ({
  chatId,
  language,
  channelType: "tutor",
  channelsCollectionName: "Tutor_Support_Channels",
});

/**
 * Parse URL pathname for messages: /{lang}/messages or /{lang}/messages/:chatId
 * (legacy /messages and /messages/:chatId supported until redirect).
 */
export const getChannelConfigFromUrl = (): ChannelConfig => {
  const pathParts = window.location.pathname.split("/").filter(Boolean);
  const [first, second, third] = pathParts;

  if (first === MESSAGES_PATH_SEGMENT) {
    return defaultChannelConfig(second || "", "en");
  }

  if (isSupportedLanguage(first) && second === MESSAGES_PATH_SEGMENT) {
    return defaultChannelConfig(third || "", first);
  }

  return defaultChannelConfig("", "en");
};

/** Base path for the messages area (used when building chat-related URLs). */
export const getCurrentBasePath = (lang: SupportedLanguage): string =>
  `/${lang}/${MESSAGES_PATH_SEGMENT}/`;

const getCookie = (cookieName: string): string | null => {
  try {
    const name = cookieName + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(";");

    for (let i = 0; i < cookieArray.length; i++) {
      const cookie = cookieArray[i].trim();
      if (cookie.indexOf(name) === 0) {
        return cookie.substring(name.length, cookie.length);
      }
    }
    return null;
  } catch (error) {
    console.error("Error read cookie:", error);
    return null;
  }
};

// const getUserInfo = () => {
//     const userInfoCookie = getCookie("userInfo");
//     if (!userInfoCookie) return null;

//     try {
//         const userInfo = JSON.parse(userInfoCookie);
//         return {
//             userId: userInfo.user_id,
//             userName: userInfo.user_full_name,
//             accessToken: userInfo.access_token,
//             refreshToken: userInfo.refresh_token,
//             accessTokenExp: userInfo?.access_token_exp,
//             refreshTokenExp: userInfo?.refresh_token_exp,
//             firebaseToken: userInfo.firebase_token
//         };
//     } catch (error) {
//         console.error("Error parsing userInfo cookie:", error);
//         return null;
//     }
// };

const setAdminInfoCookie = (data: AdminInfoAPIResponse | null): void => {
  if (!data) return;
  try {
    const { channelType } = getChannelConfigFromUrl();
    const channelRoleKey = channelType === 'tutor' ? 'tutor_support' : 'student_support';
    const userRoles = useAuthStore.getState().userRoles;
    const senderName = userRoles.includes(channelRoleKey)
      ? channelRoleKey
      : useAuthStore.getState().activeRole || channelRoleKey;
    
    const userCookie: AdminInfoCookie = {
      user_id: data?.user_id,
      email: data?.user_email,
      msg_sender_name: senderName,
      support_member_name: data?.user_name ?? "",
      firebase_token: data?.firebase_token,
      user_roles: data?.user_roles || [],
    };
    const cookieValue = encodeURIComponent(JSON.stringify(userCookie));
    document.cookie = `adminInfo=${cookieValue}; path=/; SameSite=Lax`;
    
    // Store user roles in Zustand for reactive access
    if (data?.user_roles) {
      useAuthStore.getState().setUserRoles(data.user_roles);
    }
  } catch (error) {
    console.error("Error writing adminInfo cookie:", error);
  }
};

/**
 * Clear auth-related cookies and Zustand auth state.
 */
export const clearAuthCache = (): void => {
  try {
    // Expire adminInfo cookie
    document.cookie = "adminInfo=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax";
  } catch (err) {
    console.error("Error clearing adminInfo cookie:", err);
  }

  try {
    // Clear Zustand auth store (also clears persisted localStorage)
    useAuthStore.getState().clearAuth();
  } catch (err) {
    console.error("Error clearing auth store:", err);
  }
};

/**
 * Check if user exists in supportMembers list by id/code
 */
export const isUserInSupportMembers = (userId: string | number | null | undefined, supportMembers: SupportMember[]): boolean => {
  if (!userId || !supportMembers || supportMembers.length === 0) {
    return false;
  }
  return supportMembers.some(
    (member) => String(member.code) === String(userId)
  );
};

/** Authorized role key for the current channel (messages UI uses tutor support collection by default). */
const getChannelRoleKey = (): "tutor_support" | "student_support" => {
  const { channelType } = getChannelConfigFromUrl();
  return channelType === "student" ? "student_support" : "tutor_support";
};

/**
 * Fetch roles_config from Firebase and store in auth store.
 * Returns the config or null on failure.
 */
const ensureRolesConfig = async () => {
  const store = useAuthStore.getState();
  const cached = store.rolesConfig;
  if (cached && (cached.tutor_support.length > 0 || cached.student_support.length > 0)) {
    return cached;
  }

  const { fetchRolesConfig } = await import("../../services/Messages/firestore");
  const config = await fetchRolesConfig();
  if (config) {
    store.setRolesConfig(config);
  }
  return config;
};

/**
 * Get the list of authorized roles for the current channel type from roles_config.
 */
const getAuthorizedRoles = async (): Promise<string[]> => {
  const config = await ensureRolesConfig();
  if (!config) return [];
  const key = getChannelRoleKey();
  return config[key] ?? [];
};

/**
 * Check if user has any authorized role for the current channel.
 * The authorized roles list from roles_config is the source of truth —
 * if it contains any of the user's roles, the user is valid.
 * Sets the first matching role from the user's roles as activeRole.
 */
const hasAuthorizedRole = async (userRoles: string[] | null | undefined): Promise<boolean> => {
  if (!Array.isArray(userRoles) || userRoles.length === 0) return false;

  const authorizedRoles = await getAuthorizedRoles();
  if (authorizedRoles.length === 0) return false;

  for (const role of userRoles) {
    if (authorizedRoles.includes(role)) {
      useAuthStore.getState().setActiveRole(role);
      return true;
    }
  }
  return false;
};

/**
 * Update user roles in both cookie and Zustand store
 */
export const updateUserRolesInCookie = (roles: string[] | null): void => {
  const normalizedRoles = roles || [];
  
  try {
    // Update Zustand store (single source of truth)
    useAuthStore.getState().setUserRoles(normalizedRoles);
    
    // Also update cookie for backward compatibility
    const adminCookie = getCookie("adminInfo");
    if (adminCookie) {
      const info: AdminInfoCookie = JSON.parse(adminCookie);
      info.user_roles = normalizedRoles;
      const cookieValue = encodeURIComponent(JSON.stringify(info));
      document.cookie = `adminInfo=${cookieValue}; path=/; SameSite=Lax`;
    }
  } catch (error) {
    console.error("❌ Error updating user roles:", error);
  }
};

/**
 * Redirect to signin page and clear all auth data
 */
export const redirectToSignin = async (): Promise<void> => {
  try {
    // Clear all auth data
    clearAuthCache();
    
    // Sign out from Firebase
    try {
      const { signOut } = await import("firebase/auth");
      const { auth } = await import("../../services/Messages/firebase");
      await signOut(auth);
    } catch (firebaseError) {
      console.error("Error signing out from Firebase:", firebaseError);
    }
    
    const parts = window.location.pathname.split("/").filter(Boolean);
    const langPrefix = parts[0] === "en" || parts[0] === "ar" ? parts[0] : "en";
    const signinUrl = `${window.location.origin}/${langPrefix}/signinemail`;
    window.location.href = signinUrl;
  } catch (err) {
    console.error("Error redirecting to signin:", err);
    const parts = window.location.pathname.split("/").filter(Boolean);
    const langPrefix = parts[0] === "en" || parts[0] === "ar" ? parts[0] : "en";
    window.location.href = `${window.location.origin}/${langPrefix}/signinemail`;
  }
};

/**
 * Fetch support team members from Firebase and save to Zustand store
 */
export const fetchAndSaveSupportTeamFromFirebase = async (): Promise<void> => {
  try {
    const { fetchSupportTeamMembers } = await import("../../services/Messages/firestore");
    const supportTeamMembers = await fetchSupportTeamMembers();

    if (supportTeamMembers && supportTeamMembers.length > 0) {
      const mapped = supportTeamMembers.map((m) => ({
        code: typeof m.code === "string" ? Number.parseInt(m.code, 10) : Number(m.code),
        name: String((m as { name?: string }).name ?? ""),
        roles: (m as { roles?: string[] }).roles,
      }));
      useAuthStore.getState().setSupportMembers(mapped);
    }
  } catch (error) {
    console.error("❌ Error fetching and saving support team from Firebase:", error);
  }
};

/**
 * Subscribe to current user's support document only (for role changes).
 * Does not load the full support team.
 * @param onRoleChange Optional callback when current user's role changes
 * @param onMissingRole Optional callback when the user loses the required role
 */
export const subscribeSupportTeamUpdates = async (
  userId: string | number | null | undefined,
  onRoleChange?: (roles: string[] | null) => void,
  onMissingRole?: (missingRole: string) => void
): Promise<(() => void) | null> => {
  try {
    const { subscribeSupportMemberById } = await import("../../services/Messages/firestore");
    const currentUserId = userId;

    if (!currentUserId) return null;

    let previousRoles: string[] | null = null;

    const unsubscribe = subscribeSupportMemberById(currentUserId, async (doc) => {
      try {
        if (!doc) {
          console.warn("⚠️ Current user no longer in support team.");
          const roleKey = getChannelRoleKey();
          if (onMissingRole) onMissingRole(roleKey);
          return;
        }

        const authorized = await hasAuthorizedRole(doc.roles);
        if (!authorized) {
          const roleKey = getChannelRoleKey();
          console.warn(`⚠️ Current user has no authorized role for '${roleKey}' channel.`);
          if (onMissingRole) onMissingRole(roleKey);
          return;
        }

        useAuthStore.getState().setCurrentUserSupportDoc(doc);
        const newRoles = doc.roles ?? null;
        useAuthStore.getState().setUserRoles(newRoles ?? []);

        const rolesChanged = JSON.stringify(previousRoles) !== JSON.stringify(newRoles);
        if (rolesChanged) {
          previousRoles = newRoles;
          updateUserRolesInCookie(newRoles);
          if (onRoleChange) {
            onRoleChange(newRoles);
          }
        } else {
          previousRoles = newRoles;
        }
      } catch (updateError) {
        console.error("❌ [HELPER] Error updating auth store:", updateError);
      }
    });

    return unsubscribe;
  } catch (error) {
    console.error("❌ [HELPER] Error subscribing to support team updates:", error);
    return null;
  }
};

const formatTimeFromDate = (
  date: Date,
  language: string,
  compactAmPm = false
): string => {
  let timeString = date.toLocaleTimeString(language, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  timeString = timeString.replace(
    /\s?(AM|PM)/i,
    (_, period) => (compactAmPm ? "" : " ") + period.toLowerCase()
  );
  return timeString;
};

const formatChatDate = (
  timestamp: FirestoreTimestamp | null | undefined,
  t: (key: string, fallback: string) => string,
  language: string,
  options?: { showTimeForToday?: boolean }
): string => {
  if (!timestamp || !timestamp.seconds) return "";

  const date = new Date(timestamp.seconds * 1000);
  const now = new Date();

  const isToday = date.toDateString() === now.toDateString();

  const yesterday = new Date();
  yesterday.setDate(now.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();

  if (isToday) {
    if (options?.showTimeForToday) {
      return formatTimeFromDate(date, language, true);
    }
    return t('today_label', 'Today');
  } else if (isYesterday) {
    return t('yesterday_label', 'Yesterday');
  } else {
    const options: Intl.DateTimeFormatOptions = {
      day: "numeric",
      month: "long",
    };
    if (date.getFullYear() !== now.getFullYear()) {
      options.year = "numeric";
    }
    return date.toLocaleDateString(language, options);
  }
};
const formatChatMessageTime = (
  timestamp: FirestoreTimestamp | null | undefined,
  language: string
): string => {
  if (!timestamp || !timestamp.seconds) return "";
  const date = new Date(timestamp.seconds * 1000);
  return formatTimeFromDate(date, language);
};
interface Student {
  id: string;
  name?: string;
}

const renderMentionHTML = (
  text: string = "",
  students: Student[] = [],
  userId: string | number | null | undefined
): string => {
  text = String(text);
  let isSameUserMentioned = false;
  const mentionPattern = /@{([^}_]+)(?:_([^}]*))?}/g;
  return text.replace(mentionPattern, (_, idOrKeyword, name) => {
    // Handle special mentions
    if (idOrKeyword === "everyone") {
      return `@<span class="mention">${idOrKeyword}</span>`;
    }
    // If name is missing, try to look it up in the students list
    let displayName = name;
    if (!displayName) {
      const student = students.find((s) => s.id === idOrKeyword);
      displayName = student?.name;
    }
    // If still no name, skip or return a placeholder
    if (!displayName) return "";
    isSameUserMentioned = idOrKeyword === String(userId);
    return `<span>@<span class="mention${isSameUserMentioned ? " mentioned-me" : ""
      }">${displayName}</span></span>`;
  });
};

const formatDurationFromMiliToMinutes = (durationMs: number): string => {
  const totalSeconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
    2,
    "0"
  )}`;
};

const formatMentionsMessage = (htmlContent: string): string => {
  const container = document.createElement("div");
  container.innerHTML = htmlContent;

  const mentions = container.querySelectorAll(".mention[data-mention-id]");
  mentions.forEach((span) => {
    const mentionId = span.getAttribute("data-mention-id");
    const replacement = `{${mentionId}}`;
    span.replaceWith(replacement);
  });

  return container.textContent?.trim() ?? "";
};
const handleNewLineInText = (text: string): string => {
  return text //replace new line with /n
    .replace(/<br\s*[/]?>/gi, "\n");
  //.replace(/<\/div><div>/gi, '\n')
  //.replace(/<\/p><p>/gi, '\n\n')
  //.replace(/<[^>]+>/g, '');
};
export function hasSpaceBelow(
  containerSelector: string,
  triggerEl: Element | null,
  menuSelector: string
): boolean {
  if (!triggerEl) return true;
  const menuEl = triggerEl.querySelector(menuSelector);
  if (!menuEl) return true;
  const triggerRect = triggerEl.getBoundingClientRect();
  const menuRect = menuEl.getBoundingClientRect();
  const container = document.querySelector(containerSelector);
  if (!container) return true; // fallback: assume enough space
  const containerRect = container.getBoundingClientRect();
  const spaceBelow =
    containerRect.bottom - (triggerRect.top + triggerRect.height);
  return spaceBelow >= menuRect.height;
}

/**
 * Get support members from Zustand store
 * Use this instead of userInfo.supportMemberLst
 */
export const getSupportMembers = (): SupportMember[] => {
  return useAuthStore.getState().supportMembers;
};

export {
  formatChatDate,
  formatChatMessageTime,
  formatDurationFromMiliToMinutes,
  formatMentionsMessage,
  getCookie,
  handleNewLineInText,
  renderMentionHTML,
  setAdminInfoCookie,
};
